import { pick, serverMap } from '@/shared/lib/server-map'
import type { IconName } from '@/shared/ui'
import type { ClientContact, ClientContactSource } from '@/shared/api'

/** Токен и иконка — НАШИ (сервер про палитру не знает), подпись типа приходит с сервера. */
export type ClientChannelLook = { token: string; icon: IconName }

/*
 * ⚠️ КАРТА ПО ПОЛНОМУ КЛЮЧУ, А НЕ ПО ПОДСТРОКЕ: `kind.includes('MAX')` ломается на первом же
 * значении справочника — `PHONE_MAX` содержит `MAX`, `PHONE_WHATSAPP` — `WHATSAPP`. Порядок проверок
 * стал бы носителем смысла, и перестановка двух строк меняла бы цвет половине каналов.
 *
 * Токены те же, что у плиток переписки, чтобы мессенджер не оказался в приложении двух разных
 * цветов. Скопированы значениями, а не импортом: кросс-импорт сущностей FSD запрещает, а
 * `ChannelKind` — вообще другой, более дробный словарь (двенадцать видов против шести).
 *
 * ⚠️ ПРОСТОЙ ТЕЛЕФОН — `--cyan`, А НЕ `--blue`, И ЭТО ЗАМЕР, А НЕ ВКУС. `--blue` и `--st-confirmed`
 * (телеграм) стоят на одном тоне (hue 250 у обоих), а в тёмной теме сходятся и по светлоте (0.72
 * против 0.70): плитки «Основной телефон» и «Telegram — ник» различал только глиф. Видно это было на
 * скриншоте, а не в числах контраста — гейт меряет краску на фоне, а не два фона между собой.
 * `--cyan` — hue 210, сорок градусов в стороне.
 */
const CHANNEL_LOOKS = serverMap<ClientChannelLook>({
  PHONE_PRIMARY: { token: 'var(--cyan)', icon: 'phone' },
  PHONE_SECONDARY: { token: 'var(--cyan)', icon: 'phone' },
  PHONE_VIBER: { token: 'var(--cyan)', icon: 'phone' },
  PHONE_TELEGRAM: { token: 'var(--st-confirmed)', icon: 'telegram' },
  TELEGRAM_USER_ID: { token: 'var(--st-confirmed)', icon: 'telegram' },
  TELEGRAM_USERNAME: { token: 'var(--st-confirmed)', icon: 'telegram' },
  PHONE_MAX: { token: 'var(--violet)', icon: 'external' },
  MAX_USER_ID: { token: 'var(--violet)', icon: 'external' },
  PHONE_WHATSAPP: { token: 'var(--wa)', icon: 'whatsapp' },
  WHATSAPP: { token: 'var(--wa)', icon: 'whatsapp' },
  VK_USER_ID: { token: 'var(--st-pending)', icon: 'bell' },
  EMAIL_PERSONAL: { token: 'var(--sunk)', icon: 'email' },
})

/**
 * Незнакомый тип — САМЫЙ КОНСЕРВАТИВНЫЙ вид, но канал при этом ВИДЕН: значение честное, его прислал
 * сервер (§7: `UNSPECIFIED` означает лишь, что справочник разошёлся с версией), — фронт просто не
 * выдаёт его за WhatsApp. Правило целиком — в `entities/booking/model/server-drift.ts`.
 */
const FALLBACK_LOOK: ClientChannelLook = { token: 'var(--ink-3)', icon: 'alert' }

export function clientChannelLook(kind: string): ClientChannelLook {
  return pick(CHANNEL_LOOKS, kind, FALLBACK_LOOK)
}

/** На почти белом токене иконка утонула бы — красим её чернилами. Тот же случай, что у Email. */
export function isPaleChannelTile(look: ClientChannelLook): boolean {
  return look.token === 'var(--sunk)'
}

/**
 * Атрибуция источника строкой: «Добавил менеджер · Иванов И. · другая компания · 12.03.2026».
 *
 * Слова происхождения и дату даёт СЕРВЕР; фронт добавляет ровно одно — пометку о чужой компании, и
 * она не словарь, а прочтение флага `mine`. Показывать обязательно: карточка показывает и чужие
 * каналы (§2.2), и без пометки менеджер видит незнакомый telegram, не понимая, откуда он.
 */
export function clientSourceText(source: ClientContactSource): string {
  return [
    source.originLabel,
    /*
     * ⚠️ ИМЯ — ТОЛЬКО У СВОЕГО ИСТОЧНИКА, и проверка здесь не дублирует серверную, а страхует её:
     * §5 разрешает `actorName` лишь для `MANUAL` СВОЕЙ компании, но приезжает он с чужой стороны, и
     * стоит гейтвею однажды отдать его у чужого источника — интерфейс молча покажет имя сотрудника
     * другой компании. Утечка такого рода не падает и не логируется; её видно только глазами того,
     * кому её показали.
     */
    source.mine ? source.actorName : undefined,
    source.mine ? undefined : 'другая компания',
    source.firstSeenAtLabel,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Тип и — если менеджер её задал — его метка. Метка единственное, что у канала правится (§2.5). */
export function clientContactTitle(contact: ClientContact): string {
  return contact.label ? `${contact.kindLabel} · ${contact.label}` : contact.kindLabel
}
